import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import BottomTab from "../../../components/pagination/BottomTab";
import { CardSkeleton, TableSkeleton } from "../../../components/skeleton/ListSkeleton";
import { PlanKpiKey, UnfinishedListData, getTreatments, getTreatmentsUnfinished } from "../analyticsApi";
import { changeTone, formatChange, formatNumber, formatPct, formatRs, longDate, seriesIsEmpty } from "../analyticsUtils";
import { useAnalyticsPeriod } from "../useAnalyticsPeriod";
import { useAnalyticsFetch } from "../useAnalyticsFetch";
import PeriodPicker from "../components/PeriodPicker";
import KpiCard from "../components/KpiCard";
import Funnel from "../components/Funnel";
import EmptyState from "../components/EmptyState";
import { PeriodBarChart } from "../components/charts";
import { CARD_PAD, Caption, ChartCard, EmptyRow, TD, TD_NUM, TH, TH_NUM, TR, TableCard } from "../components/ui";

const KPIS: { key: PlanKpiKey; label: string; fmt: (v: number | null) => string }[] = [
  { key: "linesCreated", label: "Lines planned", fmt: formatNumber },
  { key: "linesCompleted", label: "Lines completed", fmt: formatNumber },
  { key: "completionRate", label: "Completion rate", fmt: formatPct },
  { key: "valueCreated", label: "Value planned", fmt: formatRs },
];

const CHANGE_TONE = {
  good: "text-emerald-600 dark:text-emerald-400",
  bad: "text-rose-600 dark:text-rose-400",
  neutral: "text-slate-400 dark:text-slate-500",
};

const PAGE_SIZE = 20;

export default function AnalyticsTreatments() {
  const period = useAnalyticsPeriod();
  const { data, loading } = useAnalyticsFetch(() => getTreatments({ from: period.from, to: period.to }), [period.from, period.to]);

  const [page, setPage] = useState(1);
  const [followUp, setFollowUp] = useState<UnfinishedListData | null>(null);
  const [followUpLoading, setFollowUpLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setFollowUpLoading(true);
    getTreatmentsUnfinished(page, PAGE_SIZE)
      .then((d) => {
        if (!cancelled) setFollowUp(d);
      })
      .catch((err) => {
        console.error(err?.response || err);
        if (!cancelled) toast.error("Could not load the follow-up list");
      })
      .finally(() => {
        if (!cancelled) setFollowUpLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page]);

  const noData = !loading && data != null && seriesIsEmpty(data.series, ["linesCreated", "linesCompleted"]) && data.top.length === 0;

  return (
    <div className="space-y-4 text-slate-800 dark:text-slate-200">
      <PeriodPicker period={period} />

      {loading || !data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <CardSkeleton cards={4} height="h-28" />
        </div>
      ) : noData ? (
        <EmptyState />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {KPIS.map((k) => (
              <KpiCard key={k.key} label={k.label} value={k.fmt(data.plan[k.key]?.current ?? null)} change={data.plan[k.key]?.change ?? null} />
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartCard title="From booking to payment" hint="Each stage relative to bookings in this period">
              <Funnel stages={data.funnel} />
            </ChartCard>
            <div className="lg:col-span-2">
              <ChartCard title="Lines planned vs completed" hint={data.period.granularity === "month" ? "Per month" : "Per day"}>
                <PeriodBarChart series={data.series} specs={[{ key: "linesCreated", label: "Planned" }, { key: "linesCompleted", label: "Completed" }]} />
              </ChartCard>
            </div>
          </div>

          <TableCard title="Top treatments" hint="By value in this period · change vs previous period">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-600">
                  <th className={TH}>Treatment</th>
                  <th className={TH_NUM}>Count</th>
                  <th className={TH_NUM}>Value</th>
                  <th className={TH_NUM}>Previous value</th>
                  <th className={TH_NUM}>Change</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                {data.top.length === 0 ? (
                  <EmptyRow colSpan={5} />
                ) : (
                  data.top.map((t) => (
                    <tr key={t.key} className={TR}>
                      <td className={TD}>{t.name}</td>
                      <td className={TD_NUM}>{formatNumber(t.count)}</td>
                      <td className={TD_NUM}>{formatRs(t.value)}</td>
                      <td className={TD_NUM}>{formatRs(t.previousValue)}</td>
                      <td className={`${TD_NUM} ${CHANGE_TONE[changeTone(t.change, true)]}`}>{formatChange(t.change)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableCard>
        </>
      )}

      {data && (
        <div className={CARD_PAD}>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Unfinished plans</p>
          {data.unfinished ? (
            <>
              <p className="text-2xl font-bold tabular-nums mt-1">{formatRs(data.unfinished.value)}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {formatNumber(data.unfinished.lines)} open lines across {formatNumber(data.unfinished.patients)} patients with no upcoming appointment
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-400 dark:text-slate-500 mt-2">Not enough data yet</p>
          )}
          {data.asOfDay && <Caption>Snapshot as of {longDate(data.asOfDay)} · the list below is live</Caption>}
        </div>
      )}

      <TableCard
        title="Follow-up list"
        hint={followUp ? `${formatNumber(followUp.count)} patients · ${formatRs(followUp.total)} of planned treatment with no appointment booked` : "Patients with open planned lines and no upcoming appointment"}
        footer={<BottomTab title="patients" count={followUp?.count ?? 0} onPageChange={setPage} pageSize={PAGE_SIZE} />}
      >
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-600">
              <th className={TH}>Patient</th>
              <th className={TH}>Reg no</th>
              <th className={TH}>Mobile</th>
              <th className={TH_NUM}>Lines</th>
              <th className={TH_NUM}>Value</th>
              <th className={TH}>Treatments</th>
              <th className={TH}>Last visit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
            {followUpLoading || !followUp ? (
              <TableSkeleton rows={5} colSpan={7} />
            ) : followUp.list.length === 0 ? (
              <EmptyRow colSpan={7}>Every patient with an open plan has an appointment booked</EmptyRow>
            ) : (
              followUp.list.map((p) => (
                <tr key={p.patientId} className={TR}>
                  <td className={TD}>{p.fullName || "—"}</td>
                  <td className={TD}>{p.regNo || "—"}</td>
                  <td className={TD}>{p.mobileNo || "—"}</td>
                  <td className={TD_NUM}>{formatNumber(p.lines)}</td>
                  <td className={TD_NUM}>{formatRs(p.value)}</td>
                  <td className={`${TD} whitespace-normal max-w-xs`} title={p.treatments.join(", ")}>
                    {p.treatments.length ? p.treatments.join(", ") : "—"}
                  </td>
                  <td className={TD}>{p.lastVisit ? longDate(String(p.lastVisit).slice(0, 10)) : "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableCard>
    </div>
  );
}
